import { allExceptionDetails } from '@mimir/common/util/exception';
import { api_postRecordLog } from './api';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { writeLogEntryToConsole } from '@mimir/common/log/consoleSLogger';
import { createUuid } from '@mimir/common/util/createUuid';
import type { BackendClient } from '@mimir/common/util/backendClient';
import { abortableSleep } from '@mimir/common/async/abortableSleep';
import { isAbortError } from '@mimir/common/util/abortControl';

type LogEntry = Record<string, unknown>;

/**
 * Logger that batches messages and logs them into CloudWatch using the
 * provided api client.
 */
export class CloudLogger implements StructuredLogger {
  // Log entries which have not yet been successfully delivered.
  buffer: LogEntry[] = [];

  // Either a delivery promise, a flushing promise, or null.
  ongoing: Promise<void> | null = null;

  session = createUuid();

  // Ability to cancel pauses between posts that delivery performs.
  abortCtrl = new AbortController();

  constructor(
    private client: CloudLogApiClient,
    private writeToConsole: boolean,
    private logIntervalMs = 60_000,
  ) {
  }

  log(entry: LogEntry) {
    if (this.writeToConsole) {
      writeLogEntryToConsole(entry);
    }
    if (entry.level != 'warn' && entry.level != 'error') {
      // Only log serious problems to cloud log.
      return;
    }

    const queued = this.buffer.length;

    if (queued > 100) {
      return;
    }

    if (queued == 100) {
      entry = {
        warn: 'Log truncated'
      };
    }

    entry.when = entry.when ?? (new Date()).toUTCString();
    entry.session = this.session;
    this.buffer.push(entry);
    this.deliver();
  }

  async close() {
    // Cancel any ongoing delivery.
    this.abortCtrl.abort();
    try {
      // Wait for any ongoing delivery to cancel.
      if (this.ongoing != null) {
        await this.ongoing;
      }

      const flushing = this.flushCore();
      this.ongoing = flushing.catch(_ => {});
      await flushing;
    } finally {
      // Resume normal delivery.
      this.ongoing = null;
      this.abortCtrl = new AbortController();
      this.deliver();
    }
  }

  async flushCore() {
    while (this.buffer.length != 0) {
      await this.deliverOnce();
    }
  }

  deliver() {
    if (this.ongoing != null || this.buffer.length == 0) {
      return;
    }

    this.ongoing = this.deliverCore().catch(ex => {
      if (!isAbortError(ex)) {
        console.error('cloudLogger deliver error', ex);
      }
    });
  }

  async deliverCore() {
    try {
      while (this.buffer.length != 0) {
        try {
          await this.deliverOnce();
        } catch (ex) {
          this.log({
            ...allExceptionDetails(ex),
            level: 'warn',
            scope: 'CloudLogger.deliverOnce'
          });
        }
        await abortableSleep(this.logIntervalMs, this.abortCtrl.signal);
      }
    } finally {
      this.ongoing = null;
    }
  }

  async deliverOnce() {
    const batch = [...this.buffer];
    await this.client.postLog(batch);
    this.buffer.splice(0, batch.length);
  }
}

export function backendClientCloudLogger(client: BackendClient): CloudLogger {
  return new CloudLogger({
    postLog: async entries => {
      await api_postRecordLog(client, entries);
    }
  }, true,);
}

export interface CloudLogApiClient {
  postLog: (entries: LogEntry[]) => Promise<void>;
}
